/* global CollectJS */
import { Container, CircularProgress, Box } from "@mui/material";
import { useEffect, useMemo, useState, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PaymentInfo from "../../../components/Plan/PaymentInfo";
import Address from "../../../components/Address";
import { useFormik } from "formik";
import * as Yup from "yup";
import Summary from "../../../components/Plan/Summary";
import { handleAuth } from "../../../components/Login/Auth";
import { setItem, getItem } from "../../../utils/storage";
import moment from "moment";
import AxiosInstance from "../../AxiosInstance";
import { Grid } from "@mui/material";
import { Row, Col } from "react-bootstrap";
import showToast from "../../../components/Toast/Toster";
import { Typography } from "@mui/material";
import PayaAddCardForm from "../../../components/PayaAddCard/PayaAddCardForm";
import { useDejavooPayment } from "../../../contexts/DejavooPaymentContext";
import InputText from "../../../components/InputFields/InputText";
import { formatCardNumberDisplay, formatExpiryDisplay, getGatewayForPaymentMethod } from "../../../utils/gatewayHelper";
import BlueButton from "../../../components/Button/BlueButton";
import Previous from "../../../assets/White-sidebar-icon/Previous.svg";
import { useUserType } from "../../../contexts/UserTypeContext";
import { getTokenizationKey } from "../../../plugins/ApiHandler";
import { loadCardScript } from "../../../plugins/helpers";
import { nmiCollectPosInlineStyleConfig } from "../../../plugins/collectJsNmiPosShared";
import "./style.css";

const PlanPurchase = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { CompanyUrl } = useParams();
  const { userType } = useUserType();
  const isDonationMode = userType === "donor";

  const [companyData, setCompanyData] = useState(undefined);
  const [customerVault, setCustomerVault] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const gateway = useMemo(
    () => getGatewayForPaymentMethod(companyData, "Credit/debit card"),
    [companyData]
  );
  /** NMI plan checkout must tokenize with Collect.js then POST add-customer-card (create-customer-billing is disabled). */
  const useNmiCollectJs = useMemo(
    () => gateway === "NMI" && !location?.state?.card,
    [gateway, location?.state?.card]
  );
  const { dejavoo } = useDejavooPayment();
  const [dejavooCardData, setDejavooCardData] = useState({
    ccnumber: "",
    ccexpiry: "",
    cccvv: "",
  });
  const [dejavooCardValid, setDejavooCardValid] = useState({
    ccnumber: false,
    ccexp: false,
    cvv: false,
  });
  const [dejavooCardErrors, setDejavooCardErrors] = useState({
    ccnumber: "",
    ccexp: "",
    cvv: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!companyData && !location?.state?.card) {
        const data = await handleAuth(navigate, location);
        if (data && data?.data) {
          setCompanyData(data?.data);
          planFormik.setValues({
            CompanyId: data?.data?.CompanyId,
            FirstName: "" || "",
            LastName: "" || "",
            CardNumber: "" || "",
            ExpireMonth: "" || "",
            ExpireYear: "" || "",
            CVVNumber: "" || "",
            Address: data?.data?.Address,
            City: data?.data?.City,
            State: data?.data?.State,
            Country: data?.data?.Country,
            Zip: data?.data?.Zip,
            token: "",
            gateway: data?.data?.Gateway || "",
          });
        }
        try {
          const companyId = data?.data?.CompanyId;
          if (companyId) {
            const res = await AxiosInstance.get(
              `/v1/nmi/customer_vault/${companyId}/card`,
              { timeout: 10000 }
            );
            if (res?.status === 200 && res?.data?.data != null) {
              setCustomerVault(res.data.data);
            } else {
              setCustomerVault(undefined);
            }
          } else {
            setCustomerVault(undefined);
          }
        } catch (error) {
          setCustomerVault(undefined);
          if (error?.code === "ECONNABORTED" || error?.message?.includes("timeout")) {
            console.warn("Plan purchase: customer vault request timed out, continuing without saved card.");
          } else if (error?.response?.status === 504) {
            console.warn("Plan purchase: customer vault gateway timeout, continuing without saved card.");
          } else {
            console.error("Plan purchase: customer vault fetch failed:", error?.message);
          }
        }
      } else if (location?.state?.card) {
        const data = location?.state?.card;
        setCompanyData(data);
        const isDejavoo = (getGatewayForPaymentMethod(data, "Credit/debit card") || data?.Gateway || "").toString().toUpperCase() === "DEJAVOO";
        const companyIdForProfile = data?.CompanyId || getItem("CompanyId");
        // Dejavoo: only card has cc_number, month, year, cvv; first/last/address come from company profile (same as NMI source)
        let profileData = {};
        if ((isDejavoo || !data?.first_name) && companyIdForProfile) {
          try {
            const profileRes = await AxiosInstance.get(`/v1/user/company-profile/${companyIdForProfile}`);
            if (profileRes?.data?.success && profileRes?.data?.data?.userProfile) {
              const p = profileRes.data.data.userProfile;
              const ownerName = (p?.OwnerName || "").trim();
              const first = p?.FirstName || (ownerName ? ownerName.split(/\s+/)[0] : "") || "";
              const last = p?.LastName || (ownerName ? ownerName.split(/\s+/).slice(1).join(" ") : "") || "";
              profileData = {
                FirstName: first,
                LastName: last,
                Address: p?.Address || "",
                City: p?.City || "",
                State: p?.State || "",
                Country: p?.Country || "",
                Zip: p?.Zip || "",
              };
            }
          } catch (err) {
            console.error("Error fetching company profile for plan checkout:", err?.message);
          }
        }
        planFormik?.setValues((prevValues) => ({
          ...prevValues,
          CompanyId: data?.CompanyId,
          FirstName: data?.first_name ?? profileData?.FirstName ?? prevValues?.FirstName ?? "",
          LastName: data?.last_name ?? profileData?.LastName ?? prevValues?.LastName ?? "",
          CardNumber: data?.cc_number ?? "",
          ExpireYear: data?.cc_exp ? "20" + data.cc_exp.slice(2, 4) : "",
          ExpireMonth: data?.cc_exp?.slice(0, 2) || "",
          CVVNumber: data?.CVVNumber ?? "",
          Address: data?.address_1 ?? profileData?.Address ?? prevValues?.Address ?? "",
          City: data?.city ?? profileData?.City ?? prevValues?.City ?? "",
          State: data?.state ?? profileData?.State ?? prevValues?.State ?? "",
          Country: data?.country ?? profileData?.Country ?? prevValues?.Country ?? "",
          Zip: data?.postal_code ?? profileData?.Zip ?? prevValues?.Zip ?? "",
          token: "",
          gateway: data?.Gateway || "",
        }));
        if (isDejavoo) {
          const ccExp = data?.cc_exp || (data?.expiryDate ? String(data.expiryDate).replace("/", "") : "");
          setDejavooCardData({
            ccnumber: data?.cc_number || "",
            ccexpiry: ccExp,
            cccvv: "",
          });
        }
      }
    };

    fetchData();
  }, []);

  const [tokenizationKey, setTokenizationKey] = useState(null);
  const [collectJSInitialized, setCollectJSInitialized] = useState(false);
  const [scriptLoadingNmi, setScriptLoadingNmi] = useState(false);
  const nmiSubmitValuesRef = useRef(null);
  const nmiSubmitResetRef = useRef(null);
  const collectJsTimeoutRef = useRef(null);
  const nmiAddCardInProgressRef = useRef(false);

  const plan = location?.state?.plan;

  const planValidationSchema = useMemo(
    () =>
      Yup.object().shape({
        FirstName: Yup.string()
          .required("First name is required")
          .matches(/^[A-Za-z\s]+$/, "First name should only contain alphabetic characters"),
        LastName: Yup.string()
          .required("Last name is required")
          .matches(/^[A-Za-z\s]+$/, "Last name should only contain alphabetic characters"),
        CardNumber: Yup.string().when(["token", "gateway"], {
          is: (token, g) =>
            g === "DEJAVOO" || token || location?.state?.card || gateway === "NMI",
          then: () => Yup.string(),
          otherwise: () => Yup.string().required("Card number is required"),
        }),
        ExpireMonth: Yup.string().when("gateway", {
          is: (g) => g === "DEJAVOO" || gateway === "NMI",
          then: () => Yup.string(),
          otherwise: () => Yup.string().required("Expire month is required"),
        }),
        ExpireYear: Yup.string().when("gateway", {
          is: (g) => g === "DEJAVOO" || gateway === "NMI",
          then: () => Yup.string(),
          otherwise: () => Yup.string().required("Expire year is required"),
        }),
        Address: Yup.string().required("Address is required"),
        City: Yup.string().required("City is required"),
        State: Yup.string().required("State is required"),
        Country: Yup.string().required("Country is required"),
        Zip: Yup.string().required("Zip code is required"),
        CVVNumber: Yup.string().when("gateway", {
          is: (g) => g === "DEJAVOO" || gateway === "NMI",
          then: () => Yup.string(),
          otherwise: () =>
            Yup.string()
              .required("CVV number is required")
              .matches(/^\d{3,4}$/, "CVV must be exactly 3 or 4 digits"),
        }),
      }),
    [gateway, location?.state?.card]
  );

  const planFormik = useFormik({
    initialValues: {
      CompanyId: "",
      FirstName: "",
      LastName: "",
      CardNumber: "",
      ExpireMonth: "",
      ExpireYear: "",
      CVVNumber: "",
      Address: "",
      City: "",
      State: "",
      Country: "",
      Zip: "",
      token: "",
      gateway: "",
    },
    validationSchema: planValidationSchema,

    onSubmit: async (values, { resetForm }) => {
      let skipLoadingResetInFinally = false;
      setLoading(true);
      try {
        let tokenToUse = values?.token;
        // When user selected a Dejavoo card from Choose Plan, use that card's stored token if available; else backend will look up by billing_id
        if (gateway === "DEJAVOO" && location?.state?.card) {
          const cardToken = location?.state?.card?.token;
          if (cardToken) {
            tokenToUse = cardToken;
          }
          // If no token, we still send billing_id so backend can resolve token from CustomerVault
        }
        if (gateway === "DEJAVOO" && !location?.state?.card) {
          if (dejavoo?.status !== "loaded" || !dejavoo?.postData) {
            showToast.error(dejavoo?.error || "Payment system not ready. Wait for Dejavoo to load or refresh.");
            setLoading(false);
            return;
          }
          const cardValid = dejavooCardValid.ccnumber && dejavooCardValid.ccexp && dejavooCardValid.cvv;
          if (!cardValid) {
            showToast.error("Please enter valid card number (13–19 digits), expiry (MMYY), and CVV (3–4 digits).");
            setLoading(false);
            return;
          }
          try {
            const tokenResponse = await dejavoo.postData();
            if (!tokenResponse) {
              showToast.error("No response from payment system.");
              setLoading(false);
              return;
            }
            dejavoo.handleTransactionResponse(tokenResponse);
            const freshToken =
              tokenResponse?.payment_token_id ||
              tokenResponse?.token ||
              tokenResponse?.dccd5389;
            if (tokenResponse?.statusCode !== "00" || !freshToken) {
              showToast.error(tokenResponse?.message || "Token generation failed.");
              setLoading(false);
              return;
            }
            let displayCcNumber =
              tokenResponse?.ccNumber ||
              tokenResponse?.cc_number ||
              "";
            if (!displayCcNumber && dejavooCardData?.ccnumber) {
              const last4 = dejavooCardData.ccnumber.replace(/\D/g, "").slice(-4);
              displayCcNumber = last4 ? `************${last4}` : "";
            }
            if (!displayCcNumber) {
              showToast.error("Could not get card display number. Please try again.");
              setLoading(false);
              return;
            }
            const tokenizeRes = await AxiosInstance.post("/v1/nmi/dejavoo-tokenize-card", {
              payment_token_id: freshToken,
              CompanyId: companyData?.CompanyId,
              CustomerId: "Company",
              first_name: values?.FirstName,
              last_name: values?.LastName,
              address1: values?.Address,
              city: values?.City,
              state: values?.State,
              zip: values?.Zip,
              country: values?.Country,
              displayCcNumber,
            });
            const chdToken =
              tokenizeRes?.data?.data?.token ||
              tokenizeRes?.data?.payment_token_id ||
              tokenizeRes?.data?.token;
            if (!chdToken) {
              showToast.error(tokenizeRes?.data?.message || "Card save failed. Please try again.");
              setLoading(false);
              return;
            }
            tokenToUse = chdToken;
          } catch (err) {
            const msg =
              err?.response?.data?.message ||
              err?.response?.data?.data?.message ||
              err?.message ||
              "Failed to save card or generate token.";
            showToast.error(msg);
            setLoading(false);
            return;
          }
        }

        if (useNmiCollectJs) {
          if (scriptLoadingNmi) {
            showToast.error("Payment form is still loading. Please wait.");
            setLoading(false);
            return;
          }
          if (typeof CollectJS === "undefined" || !collectJSInitialized) {
            showToast.error("Payment form is not ready. Please refresh and try again.");
            setLoading(false);
            return;
          }
          nmiSubmitValuesRef.current = values;
          nmiSubmitResetRef.current = resetForm;
          if (collectJsTimeoutRef.current) clearTimeout(collectJsTimeoutRef.current);
          collectJsTimeoutRef.current = setTimeout(() => {
            collectJsTimeoutRef.current = null;
            setLoading(false);
            showToast.error("Card validation timed out. Check your card details and try again.");
          }, 35000);
          try {
            CollectJS.startPaymentRequest();
            skipLoadingResetInFinally = true;
          } catch (err) {
            if (collectJsTimeoutRef.current) {
              clearTimeout(collectJsTimeoutRef.current);
              collectJsTimeoutRef.current = null;
            }
            setLoading(false);
            showToast.error(err?.message || "Could not start card tokenization.");
          }
          return;
        }

        var vaultRes = "";
        // Legacy direct card POST — disabled on API for NMI; NMI uses Collect.js + add-customer-card above.
        if (!location?.state?.card && gateway !== "DEJAVOO" && gateway !== "NMI") {
          const object = {
            first_name: values?.FirstName,
            last_name: values?.LastName,
            CustomerId: "Company",
            ccnumber: values?.CardNumber,
            ccexp: `${values?.ExpireMonth}${values?.ExpireYear?.slice(2, 4)}`,
            address1: values?.Address,
            city: values?.City,
            state: values?.State,
            zip: values?.Zip,
            country: values?.Country,
            company: CompanyUrl,
            CompanyId: values?.CompanyId,
            customer_vault_id: customerVault?.customer_vault_id != null && customerVault?.customer_vault_id !== ""
              ? (typeof customerVault.customer_vault_id === "string" && String(customerVault.customer_vault_id).trim() !== "" && !/^\d+$/.test(String(customerVault.customer_vault_id).trim()) ? customerVault.customer_vault_id : Number(customerVault?.customer_vault_id))
              : "",
            type: "card",
          };
          var url = "";
          if (customerVault?.customer_vault_id) {
            url = "/v1/nmi/create-customer-billing";
          } else {
            url = "/v1/nmi/create-customer-vault";
          }
          vaultRes = await AxiosInstance.post(url, object);
        }

     
       
        const object = {
          paymentData: {
            ...(gateway === "DEJAVOO"
              ? {
                  // For Dejavoo, send token (and billing_id so backend can look up token if missing)
                  token: tokenToUse,
                  paymentToken: tokenToUse,
                  billing_id: location?.state?.card
                    ? (location?.state?.card["@attributes"]?.id ?? location?.state?.card?.billing_id)
                    : undefined,
                  gateway: "DEJAVOO",
                  paymentDoingFor: "Plan-Purchase",
                  paymentSource: "Plan Purchase",
                  paymentMethod: "card_on_file",
                  method: "card",
                  address1: values?.Address,
                  CustomerId: "Company",
                  city: values?.City,
                  state: values?.State,
                  zip: values?.Zip,
                  country: values?.Country,
                  company: CompanyUrl,
                  amount: Number(plan?.PlanPrice),
                  PlanId: plan?.PlanId,
                  CompanyId: values?.CompanyId || companyData?.CompanyId,
                  BillingDate: moment()
                    .add(1, "months")
                    .date(plan.DayOfMonth)
                    .format("MM/DD/YYYY"),
                }
              : {
                  // For other gateways, send all card details
                  billing_id: !location?.state?.card
                    ? (typeof vaultRes?.data?.data?.billing_id === "string" && String(vaultRes?.data?.data?.billing_id).trim() !== "" && !/^\d+$/.test(String(vaultRes?.data?.data?.billing_id).trim()) ? vaultRes?.data?.data?.billing_id : Number(vaultRes?.data?.data?.billing_id))
                    : (location?.state?.card["@attributes"]?.id ?? location?.state?.card?.billing_id),
                  customer_vault_id: !location?.state?.card
                    ? (typeof vaultRes?.data?.data?.customer_vault_id === "string" && String(vaultRes?.data?.data?.customer_vault_id || "").trim() !== "" && !/^\d+$/.test(String(vaultRes?.data?.data?.customer_vault_id).trim()) ? vaultRes?.data?.data?.customer_vault_id : Number(vaultRes?.data?.data?.customer_vault_id))
                    : (location?.state?.card?.customer_vault_id ?? location?.state?.card?.vaultId),
                  CardExp: `${values?.ExpireMonth}${values?.ExpireYear?.slice(
                    2,
                    4
                  )}`,
                  address1: values?.Address,
                  city: values?.City,
                  state: values?.State,
                  zip: values?.Zip,
                  country: values?.Country,
                  FirstName: values?.FirstName,
                  paymentDoingFor: "Plan-Purchase",
                  paymentSource: "Plan Purchase",
                  paymentMethod: "card_on_file",
                  method: "card",
                  CustomerId: "Company",
                  LastName: values?.LastName,
                  company: CompanyUrl,
                  amount: Number(plan?.PlanPrice),
                  PlanId: plan?.PlanId,
                  first_name: values?.FirstName,
                  last_name: values?.LastName,
                  CompanyId: values?.CompanyId,
                  BillingDate: moment()
                    .add(1, "months")
                    .date(plan.DayOfMonth)
                    .format("MM/DD/YYYY"),
                }),
          },
        };
 
        const res = await AxiosInstance.post("/v1/payment/process-payment", object);
        if (res?.data?.success === true) {
          showToast.success(res?.data?.message || res?.data?.data?.responseMessage || res?.data?.data?.responsetext || "");
          setItem("adminToken", res?.data?.token);
          if (companyData?.Gateway === "DEJAVOO") {
            setDejavooCardData({ ccnumber: "", ccexpiry: "", cccvv: "" });
            setDejavooCardValid({ ccnumber: false, ccexp: false, cvv: false });
            setDejavooCardErrors({ ccnumber: "", ccexp: "", cvv: "" });
          }

          const cleanedCompanyName =
            companyData?.CompanyName?.replace(/\s+/g, "") || "company";

          const targetPath = CompanyUrl
            ? `/${CompanyUrl}/index`
            : `/${cleanedCompanyName}/index`;

          navigate(targetPath, {
            state: {
              breadcrumbs: ["Home"],
              fromPayment: true,
              paymentSuccess: true,
            },
          });
        } else {
          const msg = res?.data?.message || res?.data?.data?.message || res?.data?.error;
          const displayMsg = (msg != null && String(msg).trim() !== "") ? String(msg).trim() : "Payment failed!";
          setLoading(false);
          showToast.error(displayMsg);
          resetForm();
        }
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.data?.message ||
          error?.response?.data?.data?.error ||
          error?.response?.data?.error ||
          (typeof error === "string" ? error : null) ||
          error?.message ||
          (gateway === "DEJAVOO"
            ? "Payment failed. Try again or check Dejavoo configuration."
            : "Payment failed. Please try again.");
        const displayMsg = (errorMessage != null && String(errorMessage).trim() !== "") ? String(errorMessage).trim() : "Payment failed. Please try again.";
        setLoading(false);
        showToast.error(displayMsg);
        resetForm();
      } finally {
        if (!skipLoadingResetInFinally) {
          setLoading(false);
        }
      }
    },
  });

  useEffect(() => {
    if (!useNmiCollectJs || !companyData?.CompanyId) {
      setTokenizationKey(null);
      setCollectJSInitialized(false);
      return;
    }
    let cancelled = false;
    setScriptLoadingNmi(true);
    getTokenizationKey(companyData.CompanyId)
      .then((keyResponse) => {
        if (cancelled) return null;
        if (keyResponse?.PublicKey) {
          return loadCardScript(keyResponse.PublicKey).then(() => keyResponse.PublicKey);
        }
        return null;
      })
      .then((key) => {
        if (!cancelled) setTokenizationKey(key || null);
      })
      .catch(() => {
        if (!cancelled) setTokenizationKey(null);
      })
      .finally(() => {
        if (!cancelled) setScriptLoadingNmi(false);
      });
    return () => {
      cancelled = true;
    };
  }, [useNmiCollectJs, companyData?.CompanyId]);

  useEffect(() => {
    if (!useNmiCollectJs || !tokenizationKey || typeof CollectJS === "undefined") {
      setCollectJSInitialized(false);
      return;
    }
    try {
      ["plan-nmi-ccnumber", "plan-nmi-ccexp", "plan-nmi-cvv"].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.innerHTML = "";
      });
      CollectJS.configure({
        tokenizationKey,
        ...nmiCollectPosInlineStyleConfig,
        fields: {
          ccnumber: {
            placeholder: "Credit Card *",
            selector: "#plan-nmi-ccnumber",
          },
          ccexp: {
            placeholder: "MM / YY *",
            selector: "#plan-nmi-ccexp",
          },
          cvv: {
            placeholder: "CVV *",
            selector: "#plan-nmi-cvv",
          },
        },
        callback: function (response) {
          if (collectJsTimeoutRef.current) {
            clearTimeout(collectJsTimeoutRef.current);
            collectJsTimeoutRef.current = null;
          }
          const values = nmiSubmitValuesRef.current;
          const resetForm = nmiSubmitResetRef.current;
          const token = response?.token ?? response?.payment_token;
          if (!values) {
            setLoading(false);
            showToast.error("Session expired. Please try again.");
            return;
          }
          if (!token) {
            setLoading(false);
            const errMsg =
              response?.error?.message ||
              response?.message ||
              "Invalid card details. Please check and try again.";
            showToast.error(errMsg);
            resetForm?.();
            return;
          }
          if (nmiAddCardInProgressRef.current) return;
          nmiAddCardInProgressRef.current = true;
          const card = response?.card || {};
          const displayCcNumber =
            (card.number && String(card.number).trim()) ||
            (card.ccnumber && String(card.ccnumber).trim()) ||
            "";
          let cardExpDigits = "";
          const rawExp =
            (card.exp && String(card.exp).replace(/\D/g, "")) ||
            (card.expiration && String(card.expiration).replace(/\D/g, "")) ||
            "";
          if (rawExp.length >= 4) cardExpDigits = rawExp.slice(0, 4);

          (async () => {
            try {
              const addRes = await AxiosInstance.post(
                "/v1/nmi/add-customer-card",
                {
                  payment_token: token,
                  type: "card",
                  first_name: values.FirstName,
                  last_name: values.LastName,
                  CompanyId: values.CompanyId || companyData?.CompanyId,
                  CustomerId: "Company",
                  Gateway: "NMI",
                  company: CompanyUrl,
                  ...(displayCcNumber ? { displayCcNumber } : {}),
                },
                { timeout: 28000 }
              );
              if (addRes?.data?.status !== 200) {
                const msg =
                  addRes?.data?.message ||
                  addRes?.data?.data?.error ||
                  "Could not save card.";
                showToast.error(msg);
                setLoading(false);
                nmiAddCardInProgressRef.current = false;
                return;
              }
              const vd = addRes?.data?.data || {};
              const billing_id =
                typeof vd.billing_id === "string" &&
                String(vd.billing_id).trim() !== "" &&
                !/^\d+$/.test(String(vd.billing_id).trim())
                  ? vd.billing_id
                  : Number(vd.billing_id);
              const customer_vault_id =
                typeof vd.customer_vault_id === "string" &&
                String(vd.customer_vault_id || "").trim() !== "" &&
                !/^\d+$/.test(String(vd.customer_vault_id).trim())
                  ? vd.customer_vault_id
                  : Number(vd.customer_vault_id);

              const CardExp =
                cardExpDigits ||
                `${values?.ExpireMonth || ""}${String(values?.ExpireYear || "").slice(-2)}`;

              const object = {
                paymentData: {
                  billing_id,
                  customer_vault_id,
                  CardExp,
                  address1: values?.Address,
                  city: values?.City,
                  state: values?.State,
                  zip: values?.Zip,
                  country: values?.Country,
                  FirstName: values?.FirstName,
                  paymentDoingFor: "Plan-Purchase",
                  paymentSource: "Plan Purchase",
                  paymentMethod: "card_on_file",
                  method: "card",
                  CustomerId: "Company",
                  LastName: values?.LastName,
                  company: CompanyUrl,
                  amount: Number(plan?.PlanPrice),
                  PlanId: plan?.PlanId,
                  first_name: values?.FirstName,
                  last_name: values?.LastName,
                  CompanyId: values?.CompanyId,
                  BillingDate: moment()
                    .add(1, "months")
                    .date(plan.DayOfMonth)
                    .format("MM/DD/YYYY"),
                },
              };

              const res = await AxiosInstance.post("/v1/payment/process-payment", object);
              if (res?.data?.success === true) {
                showToast.success(
                  res?.data?.message ||
                    res?.data?.data?.responseMessage ||
                    res?.data?.data?.responsetext ||
                    ""
                );
                setItem("adminToken", res?.data?.token);
                const cleanedCompanyName =
                  companyData?.CompanyName?.replace(/\s+/g, "") || "company";
                const targetPath = CompanyUrl
                  ? `/${CompanyUrl}/index`
                  : `/${cleanedCompanyName}/index`;
                navigate(targetPath, {
                  state: {
                    breadcrumbs: ["Home"],
                    fromPayment: true,
                    paymentSuccess: true,
                  },
                });
              } else {
                const msg =
                  res?.data?.message || res?.data?.data?.message || res?.data?.error;
                const displayMsg =
                  msg != null && String(msg).trim() !== ""
                    ? String(msg).trim()
                    : "Payment failed!";
                showToast.error(displayMsg);
                resetForm?.();
              }
            } catch (error) {
              const errorMessage =
                error?.response?.data?.message ||
                error?.response?.data?.data?.message ||
                error?.response?.data?.data?.error ||
                error?.response?.data?.error ||
                error?.message ||
                "Payment failed. Please try again.";
              showToast.error(
                String(errorMessage).trim() || "Payment failed. Please try again."
              );
              resetForm?.();
            } finally {
              setLoading(false);
              nmiAddCardInProgressRef.current = false;
            }
          })();
        },
      });
      setCollectJSInitialized(true);
    } catch (e) {
      setCollectJSInitialized(false);
    }
  }, [
    useNmiCollectJs,
    tokenizationKey,
    CompanyUrl,
    companyData?.CompanyId,
    companyData?.CompanyName,
    plan?.PlanId,
    plan?.PlanPrice,
    plan?.DayOfMonth,
    navigate,
  ]);

  useEffect(() => {
    return () => {
      if (collectJsTimeoutRef.current) {
        clearTimeout(collectJsTimeoutRef.current);
        collectJsTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (
      gateway === "DEJAVOO" &&
      companyData?.CompanyId &&
      dejavoo?.status === "uninitialized"
    ) {
      dejavoo.initialize(companyData.CompanyId);
    }
  }, [companyData?.Gateway, companyData?.CompanyId, dejavoo?.status]);

  const validateDejavooCardField = (name, value) => {
    let isValid = false;
    let errorMessage = "";
    if (name === "ccnumber") {
      const cardNumber = (value || "").replace(/\s/g, "");
      isValid = /^\d{13,19}$/.test(cardNumber);
      if (!value) errorMessage = "Credit card number is required";
      else if (!isValid) errorMessage = "Card number must be 13-19 digits";
    } else if (name === "ccexpiry") {
      const expiry = (value || "").replace(/\D/g, "");
      if (!value) errorMessage = "Expiration date is required";
      else if (!/^\d{4}$/.test(expiry)) errorMessage = "Use MMYY";
      else {
        const month = parseInt(expiry.slice(0, 2), 10);
        const year = parseInt(expiry.slice(2, 4), 10);
        const currentYear = new Date().getFullYear() % 100;
        const currentMonth = new Date().getMonth() + 1;
        if (month < 1 || month > 12) errorMessage = "Invalid month (01–12)";
        else if (year < currentYear || (year === currentYear && month < currentMonth)) errorMessage = "Expiration cannot be in the past";
        else isValid = true;
      }
    } else if (name === "cccvv") {
      isValid = /^\d{3,4}$/.test((value || "").replace(/\D/g, ""));
      if (!value) errorMessage = "CVV is required";
      else if (!isValid) errorMessage = "CVV must be 3–4 digits";
    }
    setDejavooCardValid((prev) => ({
      ...prev,
      [name === "ccexpiry" ? "ccexp" : name === "cccvv" ? "cvv" : name]: isValid,
    }));
    setDejavooCardErrors((prev) => ({
      ...prev,
      [name === "ccexpiry" ? "ccexp" : name === "cccvv" ? "cvv" : name]: errorMessage,
    }));
  };

  /** Sync validation for Dejavoo card (use on submit so we don't rely on stale state). */
  const validateDejavooCardSync = (data) => {
    const errors = { ccnumber: "", ccexp: "", cvv: "" };
    let valid = true;
    const cc = (data?.ccnumber || "").replace(/\D/g, "");
    if (!/^\d{13,19}$/.test(cc)) {
      errors.ccnumber = cc.length === 0 ? "Credit card number is required" : "Card number must be 13-19 digits";
      valid = false;
    }
    const exp = (data?.ccexpiry || "").replace(/\D/g, "").slice(0, 4);
    if (!/^\d{4}$/.test(exp)) {
      errors.ccexp = exp.length === 0 ? "Expiration date is required" : "Use MMYY";
      valid = false;
    } else {
      const month = parseInt(exp.slice(0, 2), 10);
      const year = parseInt(exp.slice(2, 4), 10);
      const cy = new Date().getFullYear() % 100;
      const cm = new Date().getMonth() + 1;
      if (month < 1 || month > 12) {
        errors.ccexp = "Invalid month (01–12)";
        valid = false;
      } else if (year < cy || (year === cy && month < cm)) {
        errors.ccexp = "Expiration cannot be in the past";
        valid = false;
      }
    }
    const cvv = (data?.cccvv || "").replace(/\D/g, "");
    if (!/^\d{3,4}$/.test(cvv)) {
      errors.cvv = cvv.length === 0 ? "CVV is required" : "CVV must be 3–4 digits";
      valid = false;
    }
    return { valid, errors };
  };

  const handleDejavooCardChange = (e) => {
    const { name, value } = e.target;
    let processed = value;
    if (name === "ccexpiry") processed = value.replace(/\D/g, "").slice(0, 4);
    else if (name === "cccvv") processed = value.replace(/\D/g, "").slice(0, 4);
    else if (name === "ccnumber") processed = (value || "").replace(/\D/g, "").slice(0, 19);
    setDejavooCardData((prev) => ({ ...prev, [name]: processed }));
    validateDejavooCardField(name, processed);
  };

  const handleChange = async (e) => {
    // Handle Dejavoo card data
    if (e.type === "dejavoo") {
      await Promise.all([
        planFormik.setFieldValue("FirstName", e.FirstName || ""),
        planFormik.setFieldValue("LastName", e.LastName || ""),
        planFormik.setFieldValue("token", e.token || ""),
        planFormik.setFieldValue("CompanyId", e.CompanyId || ""),
      ]);
      return;
    }

    // Handle regular form input
    const { name, value, type, checked } = e.target;
    // Restrict FirstName and LastName to letters and spaces only (same as add-customer)
    if (name === "FirstName" || name === "LastName") {
      if (!/^[A-Za-z\s]*$/.test(value)) return;
      await planFormik.setFieldValue(name, value);
      if (planFormik.touched[name] && planFormik.errors[name]) {
        planFormik.setFieldError(name, undefined);
      }
      return;
    }
    if (name === "Country") {
      await planFormik?.setFieldValue(name, value);
    } else if (name === "ExpireYear") {
      if (Number.parseInt(value) === currentYear) {
        setExpireMonth(
          Array.from(
            { length: 12 - currentMonth + 1 },
            (_, i) => i + currentMonth
          ) || []
        );
      } else {
        setExpireMonth(Array.from({ length: 12 }, (_, i) => i + 1) || []);
      }
      await planFormik.setFieldValue(name, value);
    } else {
      await planFormik.setFieldValue(
        name,
        type === "checkbox" ? checked : value
      );
    }
  };

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;

  const [expireMonth, setExpireMonth] = useState([]);
  const [expireYear, setExpireYear] = useState([]);

  useEffect(() => {
    const years = Array.from({ length: 10 }, (_, i) => currentYear + i) || [];
    setExpireYear(years);
    updateMonths(currentYear);
  }, [currentYear, currentMonth]);

  const updateMonths = (selectedYear) => {
    const startMonth =
      selectedYear === currentYear && !location?.state?.card ? currentMonth : 1;
    const months =
      Array.from({ length: 12 - startMonth + 1 }, (_, i) =>
        String(i + startMonth).padStart(2, "0")
      ) || [];
    setExpireMonth(months);
  };

  const handleYearChange = (value) => {
    const selectedYear = Number.parseInt(value, 10);
    updateMonths(selectedYear);
  };

  const handleZipChange = (event) => {
    const { name, value } = event.target;
    const regex = /^[A-Za-z0-9]*$/;

    if (regex.test(value) || value === "") {
      planFormik.setFieldValue(name, value);
    }
  };

  return (
    <Grid className="plan-purchase-full-page">
      <Container className="py-4 px-3 palnCheckoutSub" maxWidth={false} disableGutters={false}>
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
          <BlueButton
            className="plan-purchase-back-btn text-white-color d-flex align-items-center justify-content-center"
            isDonationMode={isDonationMode}
            label={<img src={Previous} alt="Back" style={{ width: "20px", height: "20px" }} />}
            onClick={() => navigate(-1)}
            style={{
              marginRight: "10px",
              width: "50px",
              height: "40px",
              marginBottom: "0",
              padding: "0",
              borderRadius: "4px",
            }}
          />
          <Typography className="heading-two text-blue-color mb-0">
            Checkout
          </Typography>
          <div style={{ width: "50px" }} />
        </div>

        <Row className="row g-0">
          <Col
            className="col-lg-7 px-lg-4 px-0 plan-purchase-form-col"
            lg={7}
          >
                <Typography className="paymentInfoLike heading-four two text-blue-color">
                  Payment Information
                </Typography>

                {gateway === "DEJAVOO" ? (
                  <>
                    {(dejavoo?.status === "loading" || dejavoo?.status === "uninitialized") && (
                      <Grid className="d-flex justify-content-center align-items-center p-4" style={{ minHeight: "120px" }}>
                        <CircularProgress size={40} style={{ color: "var(--bg-blue-color)" }} />
                        <Typography className="ms-3 text-blue-color">Loading Dejavoo payment...</Typography>
                      </Grid>
                    )}
                    {dejavoo?.status === "error" && (
                      <Typography variant="body2" color="error" sx={{ py: 2 }}>{dejavoo?.error || "Failed to load payment."}</Typography>
                    )}
                    {dejavoo?.status === "loaded" && (
                      <>
                        <Grid container spacing={3} className="mt-2 plan-purchase-dejavoo-fields">
                          <Grid
                            className="plan-checkout-field-item"
                            size={{
                              xs: 12,
                              sm: 6
                            }}>
                            <InputText
                              name="FirstName"
                              label="First Name"
                              value={planFormik?.values?.FirstName}
                              onChange={handleChange}
                              error={planFormik?.touched?.FirstName && Boolean(planFormik?.errors?.FirstName)}
                              helperText={planFormik?.touched?.FirstName && planFormik?.errors?.FirstName}
                              fullWidth
                              className="text-blue-color w-100"
                            />
                          </Grid>
                          <Grid
                            className="plan-checkout-field-item"
                            size={{
                              xs: 12,
                              sm: 6
                            }}>
                            <InputText
                              name="LastName"
                              label="Last Name"
                              value={planFormik?.values?.LastName}
                              onChange={handleChange}
                              error={planFormik?.touched?.LastName && Boolean(planFormik?.errors?.LastName)}
                              helperText={planFormik?.touched?.LastName && planFormik?.errors?.LastName}
                              fullWidth
                              className="text-blue-color w-100"
                            />
                          </Grid>
                        </Grid>
                        <Box className="dejavoo-native-card-fields">
                        <Grid container spacing={3} className="mt-2">
                          <Grid className="plan-checkout-field-item" size={12}>
                            <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px" }}>
                              <label className="cs-rs__label" htmlFor="ccnumber">
                                Credit Card
                                <span className="cs-field-label-required" aria-hidden="true">
                                  {" "}
                                  *
                                </span>
                              </label>
                            <InputText
                              name="ccnumber"
                              placeholder="13–19 digits"
                              value={formatCardNumberDisplay(dejavooCardData.ccnumber)}
                              onChange={handleDejavooCardChange}
                              error={!!dejavooCardErrors.ccnumber}
                              fullWidth
                              className="text-blue-color w-100"
                              sx={{
                                "& .MuiOutlinedInput-root fieldset": {
                                  borderColor: dejavooCardValid.ccnumber && dejavooCardData.ccnumber ? "var(--border-green-color) !important" : (dejavooCardErrors.ccnumber ? "var(--bg-red-color) !important" : undefined),
                                  borderTopColor: dejavooCardValid.ccnumber && dejavooCardData.ccnumber ? "var(--border-green-color) !important" : (dejavooCardErrors.ccnumber ? "var(--bg-red-color) !important" : undefined),
                                },
                                "& .MuiInputBase-input": {
                                  color: dejavooCardValid.ccnumber && dejavooCardData.ccnumber ? "var(--text-blue-color) !important" : (dejavooCardErrors.ccnumber ? "var(--bg-red-color) !important" : undefined),
                                },
                              }}
                            />
                            </Box>
                          </Grid>
                          <Grid className="plan-checkout-field-item" size={6}>
                            <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px" }}>
                              <label className="cs-rs__label" htmlFor="ccexpiry">
                                MM / YY
                                <span className="cs-field-label-required" aria-hidden="true">
                                  {" "}
                                  *
                                </span>
                              </label>
                            <InputText
                              name="ccexpiry"
                              placeholder="MM/YY"
                              value={formatExpiryDisplay(dejavooCardData.ccexpiry)}
                              onChange={handleDejavooCardChange}
                              error={!!dejavooCardErrors.ccexp}
                              fullWidth
                              className="text-blue-color w-100"
                              sx={{
                                "& .MuiOutlinedInput-root fieldset": {
                                  borderColor: dejavooCardValid.ccexp && dejavooCardData.ccexpiry ? "var(--border-green-color) !important" : (dejavooCardErrors.ccexp ? "var(--bg-red-color) !important" : undefined),
                                  borderTopColor: dejavooCardValid.ccexp && dejavooCardData.ccexpiry ? "var(--border-green-color) !important" : (dejavooCardErrors.ccexp ? "var(--bg-red-color) !important" : undefined),
                                },
                                "& .MuiInputBase-input": {
                                  color: dejavooCardValid.ccexp && dejavooCardData.ccexpiry ? "var(--text-blue-color) !important" : (dejavooCardErrors.ccexp ? "var(--bg-red-color) !important" : undefined),
                                },
                              }}
                            />
                            </Box>
                          </Grid>
                          <Grid className="plan-checkout-field-item" size={6}>
                            <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px" }}>
                              <label className="cs-rs__label" htmlFor="cccvv">
                                CVV
                                <span className="cs-field-label-required" aria-hidden="true">
                                  {" "}
                                  *
                                </span>
                              </label>
                            <InputText
                              name="cccvv"
                              placeholder="3–4 digits"
                              value={dejavooCardData.cccvv}
                              onChange={handleDejavooCardChange}
                              error={!!dejavooCardErrors.cvv}
                              fullWidth
                              className="text-blue-color w-100"
                              sx={{
                                "& .MuiOutlinedInput-root fieldset": {
                                  borderColor: dejavooCardValid.cvv && dejavooCardData.cccvv ? "var(--border-green-color) !important" : (dejavooCardErrors.cvv ? "var(--bg-red-color) !important" : undefined),
                                  borderTopColor: dejavooCardValid.cvv && dejavooCardData.cccvv ? "var(--border-green-color) !important" : (dejavooCardErrors.cvv ? "var(--bg-red-color) !important" : undefined),
                                },
                                "& .MuiInputBase-input": {
                                  color: dejavooCardValid.cvv && dejavooCardData.cccvv ? "var(--text-blue-color) !important" : (dejavooCardErrors.cvv ? "var(--bg-red-color) !important" : undefined),
                                },
                              }}
                            />
                            </Box>
                          </Grid>
                        </Grid>
                        </Box>
                        <Typography className="paymentInfoLike heading-four plan-checkout-billing-title text-blue-color">
                          Billing Address
                        </Typography>
                        <Address
                          formik={planFormik}
                          handleChange={handleChange}
                          handleZipChange={handleZipChange}
                        />
                      </>
                    )}
                  </>
                ) : companyData?.Gateway === "PAYA" ? (
                  <PayaAddCardForm
                    onSuccess={(cardData) => {
                      handleChange(cardData);
                    }}
                    CompanyId={companyData?.CompanyId}
                  />
                ) : useNmiCollectJs ? (
                  <>
                    {(scriptLoadingNmi ||
                      (tokenizationKey && !collectJSInitialized)) && (
                      <Grid
                        className="d-flex justify-content-center align-items-center p-4"
                        style={{ minHeight: "120px" }}
                      >
                        <CircularProgress
                          size={40}
                          style={{ color: "var(--bg-blue-color)" }}
                        />
                        <Typography className="ms-3 text-blue-color">
                          Loading secure card form…
                        </Typography>
                      </Grid>
                    )}
                    {!scriptLoadingNmi && !tokenizationKey && (
                      <Typography variant="body2" color="error" sx={{ py: 2 }}>
                        Could not load the payment form. Confirm NMI is configured and a
                        public tokenization key exists for this company.
                      </Typography>
                    )}
                    {tokenizationKey && (
                      <>
                        <Grid container spacing={2} className="mt-2 plan-purchase-nmi-fields">
                          <Grid
                            className="plan-checkout-field-item"
                            size={{
                              xs: 12,
                              sm: 6
                            }}>
                            <InputText
                              name="FirstName"
                              label="First Name"
                              value={planFormik?.values?.FirstName}
                              onChange={handleChange}
                              error={
                                planFormik?.touched?.FirstName &&
                                Boolean(planFormik?.errors?.FirstName)
                              }
                              helperText={
                                planFormik?.touched?.FirstName &&
                                planFormik?.errors?.FirstName
                              }
                              fullWidth
                              className="text-blue-color w-100"
                            />
                          </Grid>
                          <Grid
                            className="plan-checkout-field-item"
                            size={{
                              xs: 12,
                              sm: 6
                            }}>
                            <InputText
                              name="LastName"
                              label="Last Name"
                              value={planFormik?.values?.LastName}
                              onChange={handleChange}
                              error={
                                planFormik?.touched?.LastName &&
                                Boolean(planFormik?.errors?.LastName)
                              }
                              helperText={
                                planFormik?.touched?.LastName &&
                                planFormik?.errors?.LastName
                              }
                              fullWidth
                              className="text-blue-color w-100"
                            />
                          </Grid>
                          <Grid className="plan-checkout-field-item" size={12}>
                            <Typography
                              variant="caption"
                              className="text-blue-color d-block mb-1"
                            >
                              Card number
                            </Typography>
                            <div id="plan-nmi-ccnumber" className="payment-field" />
                          </Grid>
                          <Grid
                            className="plan-checkout-field-item"
                            size={{
                              xs: 12,
                              sm: 6
                            }}>
                            <Typography
                              variant="caption"
                              className="text-blue-color d-block mb-1"
                            >
                              Expiration
                            </Typography>
                            <div id="plan-nmi-ccexp" className="payment-field" />
                          </Grid>
                          <Grid
                            className="plan-checkout-field-item"
                            size={{
                              xs: 12,
                              sm: 6
                            }}>
                            <Typography
                              variant="caption"
                              className="text-blue-color d-block mb-1"
                            >
                              CVV
                            </Typography>
                            <div id="plan-nmi-cvv" className="payment-field" />
                          </Grid>
                        </Grid>
                        <Typography className="paymentInfoLike heading-four plan-checkout-billing-title text-blue-color">
                          Billing Address
                        </Typography>
                        <Address
                          formik={planFormik}
                          handleChange={handleChange}
                          handleZipChange={handleZipChange}
                        />
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <PaymentInfo
                      formik={planFormik}
                      handleChange={handleChange}
                      expireMonth={expireMonth}
                      expireYear={expireYear}
                      handleYearChange={handleYearChange}
                      disabled={false}
                    />
                    <Typography className="paymentInfoLike heading-four plan-checkout-billing-title text-blue-color">
                      Billing Address
                    </Typography>
                    <Address
                      formik={planFormik}
                      handleChange={handleChange}
                      handleZipChange={handleZipChange}
                    />
                  </>
                )}
          </Col>
          <Col
            className="col-lg-5 px-lg-4 px-0 d-flex flex-column align-items-center justify-content-center gap-3"
            lg={5}
          >
            <Summary
                  formik={planFormik}
                  plan={plan}
                  loading={loading}
                  isDonationMode={isDonationMode}
                  dejavooDisabled={
                    (gateway === "DEJAVOO" && dejavoo?.status !== "loaded") ||
                    (useNmiCollectJs &&
                      (scriptLoadingNmi ||
                        !tokenizationKey ||
                        !collectJSInitialized))
                  }
                  onAddSubscriptionClick={
                    gateway === "DEJAVOO" && !location?.state?.card
                      ? () => {
                          console.log("[PlanPurchase] Add Subscription clicked (Dejavoo new card)");
                          const { valid, errors: syncErrors } = validateDejavooCardSync(dejavooCardData);
                          if (!valid) {
                            setDejavooCardErrors(syncErrors);
                            setDejavooCardValid({
                              ccnumber: !syncErrors.ccnumber,
                              ccexp: !syncErrors.ccexp,
                              cvv: !syncErrors.cvv,
                            });
                            const firstError = syncErrors.ccnumber || syncErrors.ccexp || syncErrors.cvv;
                            showToast.error(firstError || "Please enter valid card number, expiry (MM/YY), and CVV.");
                            return;
                          }
                          planFormik.setValues(
                            {
                              ...planFormik.values,
                              CardNumber: dejavooCardData.ccnumber,
                              ExpireMonth: dejavooCardData.ccexpiry?.slice(0, 2) || "",
                              ExpireYear: dejavooCardData.ccexpiry?.length >= 4 ? "20" + dejavooCardData.ccexpiry.slice(2, 4) : "",
                              CVVNumber: dejavooCardData.cccvv,
                              gateway: "DEJAVOO",
                            },
                            false
                          );
                          setTimeout(() => planFormik.handleSubmit(), 0);
                        }
                      : undefined
                  }
                />

            <Typography
              className="text-blue-color"
              style={{
                fontSize: "10px",
                opacity: 0.6,
                fontWeight: 400,
              }}
            >
              By proceeding, you agree to our Privacy Policy and Terms of
              Service.
            </Typography>
          </Col>
        </Row>
      </Container>
    </Grid>
  );
};

export default PlanPurchase;
